import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IonButton, IonIcon } from '@ionic/react';
import { settings, close } from 'ionicons/icons';
import { setTestMode as setTeamTestMode } from '@/services/teamService';
import { setTestMode as setProgressTestMode } from '@/services/progressService';
import { setTestMode as setCourseTestMode, courseService } from '@/services/courseService';
import { setMysteryShopMock } from '@/services/mysteryShopService';
import { useRefresh } from '@/contexts/RefreshContext';
import { PopoverMenuComponent } from '@/components/ui/PopoverMenuComponent';
import { useUserCourseContext } from '@/contexts/UserCourseContext';
import { useProfileStore } from '@/store/useProfileStore';
import { apiService } from '@/services/apiService';
import { showError } from '@/services/toastService';
import { Lesson, LessonType } from '@/types/userCourse';

export const TestPanelDesktopSidebar: React.FC = () => {
  const location = useLocation();
  const [testDataValue, setTestDataValue] = useState(0);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const { triggerRefresh, registerResetHandler } = useRefresh();
  const [showCertificationPopover, setShowCertificationPopover] = useState(false);
  const [showCoursePopover, setShowCoursePopover] = useState(false);
  const [certificationPopoverEvent, setCertificationPopoverEvent] = useState<Event | undefined>();
  const [coursePopoverEvent, setCoursePopoverEvent] = useState<Event | undefined>();
  const { userCourse, currentLessonType } = useUserCourseContext();
  const { activeTeam, activeProfile } = useProfileStore();

  // Determine if Test Panel should be shown based on route
  let showTestPanel = false;
  let showTestData = false;
  let showTestDataWidget2 = false;
  let showResetUserCertification = false;
  let showResetUserCourse = false;
  let showGenerateError = false;
  let showUserLessonsJson = false;
  let showMSTestData = false;

  if (location.pathname.startsWith('/course')) {
    showTestPanel = true;
    showResetUserCourse = true;
    showUserLessonsJson = true;
  } else if (location.pathname.includes('/mystery-shop')) {
    showTestPanel = true;
    showMSTestData = true;
  } else {
    switch (location.pathname) {
      case '/dashboard':
        showTestPanel = true;
        showTestData = true;
        showGenerateError = true;
        break;
      case '/all-courses':
        showTestPanel = true;
        showTestData = true;
        showTestDataWidget2 = false;
        showResetUserCertification = true;
        break;
      case '/my-team':
        showTestPanel = true;
        showTestData = true;
        showTestDataWidget2 = true;
        break;
      case '/my-progress':
        showTestPanel = true;
        showTestData = true;
        showTestDataWidget2 = true;
        break;
      default:
        showTestPanel = false;
        showTestData = false;
        showTestDataWidget2 = false;
        break;
    }
  }

  // Register reset handler on mount
  useEffect(() => {
    const resetHandler = () => {
      setTestDataValue(0);
      setTeamTestMode(0);
      setProgressTestMode(0);
      setCourseTestMode(0);
      setMysteryShopMock(false);
    };
    registerResetHandler(resetHandler);
  }, [registerResetHandler]);

  // Reset state when route changes - use useLayoutEffect to run BEFORE page useEffects
  useLayoutEffect(() => {
    setTeamTestMode(0);
    setProgressTestMode(0);
    setCourseTestMode(0);

    // Default mock data ON for demo teams on mystery-shop route
    if (location.pathname.includes('/mystery-shop') && activeTeam?.demo) {
      setTestDataValue(1);
      setMysteryShopMock(true);
    } else {
      setTestDataValue(0);
      setMysteryShopMock(false);
    }
  }, [location.pathname, activeTeam?.demo]);

  // Don't render anything if Test Panel should not be shown
  if (!showTestPanel) {
    return null;
  }

  const handleToggleTestData = async () => {
    // Toggle between 0 and 1
    const newValue = testDataValue === 0 ? 1 : 0;

    // Check if team is required for My Team page (only when turning ON test data)
    if (location.pathname === '/my-team' && !activeTeam && newValue > 0) {
      showError('You must create a Team before using this feature.');
      return;
    }

    setTestDataValue(newValue);

    // Set test mode based on current route
    switch (location.pathname) {
      case '/my-team':
        setTeamTestMode(newValue);
        setProgressTestMode(newValue);
        await triggerRefresh('/my-team');
        break;
      case '/my-progress':
        setProgressTestMode(newValue);
        await triggerRefresh('/my-progress');
        break;
      case '/all-courses':
        setCourseTestMode(newValue);
        await triggerRefresh('/all-courses');
        break;
      case '/dashboard':
        setProgressTestMode(newValue);
        setCourseTestMode(newValue);
        await triggerRefresh('/dashboard');
        break;
    }
  };

  const handleToggleMSTestData = async () => {
    const newValue = testDataValue === 0 ? 1 : 0;
    setTestDataValue(newValue);
    setMysteryShopMock(newValue === 1);
    await triggerRefresh('/mystery-shop');
  };

  const handleToggleWidgets = async () => {
    // Toggle between 1 and 2 (only works when testDataValue is already > 0)
    const newValue = testDataValue === 1 ? 2 : 1;
    setTestDataValue(newValue);

    // Set test mode based on current route
    switch (location.pathname) {
      case '/my-team':
        setTeamTestMode(newValue);
        setProgressTestMode(newValue);
        await triggerRefresh('/my-team');
        break;
      case '/my-progress':
        setProgressTestMode(newValue);
        await triggerRefresh('/my-progress');
        break;
      case '/dashboard':
        setProgressTestMode(newValue);
        setCourseTestMode(newValue);
        await triggerRefresh('/dashboard');
        break;
    }
  };

  const handleResetCertification = (e: React.MouseEvent) => {
    setCertificationPopoverEvent(e.nativeEvent);
    setShowCertificationPopover(true);
  };

  const handleCertificationPopoverDismiss = async (selectedKey?: string) => {
    setShowCertificationPopover(false);
    if (selectedKey === 'confirm') {
      try {
        await courseService.deleteUserCertifications();
        // Reload the browser page to flush everything
        window.location.reload();
      } catch (error) {
        // Error modal presented by API
      }
    }
  };

  const handleResetCourse = (e: React.MouseEvent) => {
    setCoursePopoverEvent(e.nativeEvent);
    setShowCoursePopover(true);
  };

  const handleCoursePopoverDismiss = async (selectedKey?: string) => {
    setShowCoursePopover(false);
    if (selectedKey === 'confirm') {
      try {
        if (!userCourse) {
          return;
        }
        await courseService.deleteUserCourse(userCourse.certificationId, userCourse.courseId);
        // Reload the browser page to flush everything
        window.location.reload();
      } catch (error) {
        showError('Failed to reset course.');
      }
    }
  };

  const handleGenerateError = async () => {
    try {
      await apiService.get('/ping?code=500');
    } catch (error) {
      // Error modal will be shown automatically by apiService
    }
  };

// Get live userLessons data from the store
  const userLessons = userCourse?.userLessons || [];

  // If panel should not be shown at all on this route, return null
  if (!showTestPanel) {
    return null;
  }

  // Show collapsed gear icon when not expanded
  if (!isPanelExpanded) {
    return (
      <div className="w-full p-2 flex justify-start items-end z-10 mb-2">
        <button
          onClick={() => setIsPanelExpanded(true)}
          className="opacity-30 hover:opacity-60 transition-opacity"
          aria-label="Open Test Panel"
        >
          <IonIcon icon={settings} style={{ fontSize: '16px' }} className="text-gray-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-full p-4 z-10 mb-2">
      <div
        className="bg-gray-100 rounded-lg flex flex-col"
        style={{ height: showUserLessonsJson ? '450px' : '218px' }}
      >
        <div className="p-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-semibold">Test Panel</h3>
            <button
              onClick={() => setIsPanelExpanded(false)}
              className="w-6 h-6 hover:bg-gray-300 rounded flex items-center justify-center transition-colors"
              aria-label="Close Test Panel"
            >
              <IonIcon icon={close} className="text-gray-700" />
            </button>
          </div>
          {/* Button area */}
          {showMSTestData && (
            <div className="flex items-center gap-2 mb-2">
              <IonButton
                onClick={handleToggleMSTestData}
                color="secondary"
                fill={testDataValue === 0 ? 'outline' : 'solid'}
                size="small"
              >
                MS Test Data
              </IonButton>
            </div>
          )}
          {showTestData && (
            <div className="flex items-center gap-2 mb-2">
              <IonButton
                onClick={handleToggleTestData}
                color="secondary"
                fill={testDataValue === 0 ? 'outline' : 'solid'}
                size="small"
              >
                Use Test Data
              </IonButton>
              {showTestDataWidget2 && (
                <IonButton
                  onClick={handleToggleWidgets}
                  color="secondary"
                  fill={testDataValue === 1 ? 'outline' : testDataValue === 2 ? 'solid' : 'outline'}
                  size="small"
                  disabled={testDataValue === 0}
                >
                  2
                </IonButton>
              )}
            </div>
          )}
          {showResetUserCertification && (
            <div className="flex flex-col gap-2 mb-3">
              <IonButton
                onClick={handleResetCertification}
                color="secondary"
                fill="outline"
                size="small"
              >
                Reset Certifications
              </IonButton>
            </div>
          )}
          {showResetUserCourse && (
            <div className="flex flex-col gap-2 mb-3">
              <IonButton
                onClick={handleResetCourse}
                color="secondary"
                fill="outline"
                size="small"
              >
                Reset Course
              </IonButton>
              {currentLessonType === 'video' && (
                <IonButton
                  onClick={() => window.dispatchEvent(new CustomEvent('test-panel-go-to-end'))}
                  color="secondary"
                  fill="outline"
                  size="small"
                >
                  Go To 98%
                </IonButton>
              )}
            </div>
          )}
          {showGenerateError && (
            <div className="mt-2">
              <IonButton
                onClick={handleGenerateError}
                color="medium"
                fill="solid"
                size="small"
              >
                Generate Error
              </IonButton>
            </div>
          )}
        </div>

        {/* JSON Data Area - only show for course routes */}
        {showUserLessonsJson && (
          <div className="flex-1 min-h-0 px-4 pb-4">
            <div className="h-full text-[10px] bg-white p-1 rounded border border-gray-300 font-mono leading-tight whitespace-nowrap overflow-auto">
              {userCourse ? (
                <>
                  <div className="mb-3">
                    lastAccessedLesson: {userCourse.lastAccessedLesson || 'null'}
                    <br />
                    progress: {userCourse.progress || 'null'}
                  </div>
                  {userLessons.length > 0 ? (
                    <>
                      {userLessons.map((lesson, index) => (
                        <div key={lesson.lessonId} className={index > 0 ? 'mt-3' : ''}>
                          {Object.entries(lesson).map(([key, value]) => (
                            <div key={key}>
                              {key}: {value === null ? 'null' : String(value)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-yellow-600">No lessons started yet</div>
                  )}
                </>
              ) : (
                <div className="text-gray-500">No userCourse data (context not available)</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Certification Reset Confirmation Popover */}
      <PopoverMenuComponent
        isOpen={showCertificationPopover}
        event={certificationPopoverEvent}
        alignment="end"
        onDismiss={handleCertificationPopoverDismiss}
        menuItems={[
          { key: 'confirm', value: 'Click to confirm that you want to delete all user certifications.' }
        ]}
      />

      {/* Course Reset Confirmation Popover */}
      <PopoverMenuComponent
        isOpen={showCoursePopover}
        event={coursePopoverEvent}
        alignment="end"
        onDismiss={handleCoursePopoverDismiss}
        menuItems={[
          { key: 'confirm', value: 'Click to confirm that you want to restart this course from scratch.' }
        ]}
      />
    </div>
  );
};
